import _ from "lodash";
import React from "react";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";
import ACLStore from "lib/aclStore";

import { getYearDurationFormat, isIdEqual } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";
import CurriculumProgramLogo from "UIComponents/CurriculumProgramLogo";
import { AddOutlined } from "@toddle-edu/ds-icons";
import { Avatar, Button } from "@toddle-edu/ds-web";
import classes from "./ChildSelector.scss";

import { colors } from "Constants";
import { goToPlatformHomeRoute } from "modules/NavigationModule";
import { getUserIdentifierName } from "Utils/GetFormattedUserName";
import VerifyOtpModal from "AppComponents/VerifyOtpModal";
import { getPlatformUserSessionDetailsQuery } from "Platform/modules/PlatformQuery";
import { getPlatformUserSessionDetailsFromCache } from "Platform/modules/PlatformGraphqlHelpers";
import { updateSelectedChild } from "Teacher/routes/Courses/modules/CoursesModule";
import {
  getUserInfo,
  updateSessionVerificationDetails,
  verifySessionWithServer,
  generateEmailOTPForSessionVerification,
  getOrgWorkSpaceDetails,
} from "Login/modules/LoginModule";

const styles = {
  addButtonStyle: {
    padding: "4px",
    height: "auto",
    width: "auto",
    borderRadius: "50%",
  },
  customTextStyle: {
    fontSize: "1rem",
  },
  selectedChildProfile: {
    border: `2px solid ${colors.borderSelected}`,
  },
};

const childNameContainerClasses = classNames(
  classes.childNameContainer,
  "group"
);

const ChildSelector = ({
  allChildren,
  onChildChangeClick,
  onAddChildClick,
  addChildText,
  childID,
  userAcademicYears,
  setAcademicYearSelected,
  selectedAcademicYear,
  t,
  goToRelativeRoute,
  organizationName,
  childProfileImage,
  childName,
  userCurriculumPrograms,
  currentCurriculumProgramId,
  goToPlatformHomeRoute,
  isChildBlocked,
  isSessionVerified,
  sessionVerificationDetails,
  updateSessionVerificationDetails,
  verifySessionWithServer,
  generateEmailOTPForSessionVerification,
  organizationId,
  selectedChild,
  updateSelectedChild,
}) => {
  const changeAcademicYear = ({ selectedYear }) => {
    goToRelativeRoute({
      route: "/",
    });
    setAcademicYearSelected(selectedYear);

    /* ------BELOW CODE COULD BE USEFUL IN FUTURE------
      // update selectedAcademicYearId in local storage, because of which the academic year will persist on refresh
      const selectedYearId = _.get(selectedYear, "id");
      if (selectedYearId) {
        manageAcademicYear.setInLocalStorage(selectedYearId);
      }
      */
  };

  const reverseUserAcademicYears = _.reverse([...userAcademicYears]);

  const verifyUserSession = async ({ child }) => {
    const childOrganizationId = _.get(child, "organization.id", "");
    updateSelectedChild(child);
    if (isSessionVerified || isIdEqual(childOrganizationId, organizationId)) {
      onChildChangeClick({ id: child.id });
    } else {
      const { isSuccess } = await generateEmailOTPForSessionVerification();
      if (isSuccess) {
        updateSessionVerificationDetails({ isVisible: true });
      }
    }
  };

  const handleVerifySessionWithServer = async () => {
    const { isSuccess } = await verifySessionWithServer();
    if (isSuccess) {
      const childId = _.get(selectedChild, "id", "");
      onChildChangeClick({ id: childId, reload: true });
    }
  };

  const onVerifyOtpModalClose = () => {
    updateSessionVerificationDetails({ isVisible: false, otp: "" });
  };

  const componentTestId = "homepage-childSelector";

  return (
    <div className={classes.childContainer} data-test-id={componentTestId}>
      <div
        className={classes.currentChildContainer}
        data-test-id="homepage-child-switcher"
      >
        <div className={classes.profileImage}>
          <Avatar
            shade={"light"}
            iconType={"individual"}
            size={"medium"}
            name={childName}
            src={childProfileImage}
          />
        </div>
        <div className={classes.textContainer}>
          <div className={childNameContainerClasses}>
            <div className={classes.childNameText}>{childName}</div>
          </div>
          <div className={classes.orgNameText}>{organizationName}</div>
        </div>
      </div>
      <div className={classes.childBoxContainer}>
        {/* AddChild Button */}

        <div
          className={classes.childCard}
          data-test-id={`${componentTestId}-addChild-button`}
        >
          <Button
            variant={"plain"}
            onClick={onAddChildClick}
            style={styles.addButtonStyle}
          >
            <div className={classes.addLeftCircle}>
              <AddOutlined variant="on" />
            </div>
          </Button>
          <div className={classes.childText}>{addChildText}</div>
        </div>

        {/* Childrens */}
        {_.map(allChildren, child => {
          const childTextClass = classNames({
            [classes.childText]: true,
            [classes.selectedChildText]: child.id === childID,
          });

          const profileCustomStyle = {
            ...(child.id == childID && styles.selectedChildProfile),
          };

          const onChildChangeClickHandler = () => {
            verifyUserSession({ child });
          };

          const childIdentifier = getUserIdentifierName({ user: child });

          return (
            <div
              className={classes.childCard}
              onClick={onChildChangeClickHandler}
              key={child.id}
              data-test-id={`homepage-child-switcher-child-${child.id}`}
            >
              <div className={classes.leftCircle} style={profileCustomStyle}>
                <Avatar
                  shade={"light"}
                  iconType={"individual"}
                  size={"medium"}
                  name={childIdentifier}
                  src={child.profileImage}
                />
              </div>
              <div className={childTextClass}>{childIdentifier}</div>
            </div>
          );
        })}
      </div>
      {ACLStore.can("FeatureFlag:AcademicYearSwitcher") && !isChildBlocked ? (
        <div className={classes.academicContainer}>
          <div className={classes.academicHeader}>
            {t("common:select_with_label", {
              label: t("common:academic_year"),
            })}
          </div>
          <div className={classes.academicYearContainer}>
            {_.map(!isChildBlocked ? reverseUserAcademicYears : [], year => {
              const { id, startDate, endDate } = year;
              const label = getYearDurationFormat({ startDate, endDate });

              const academicYearClass = classNames({
                [classes.academicYear]: true,
                [classes.selectedAcademicYear]: _.isEqual(
                  id,
                  _.get(selectedAcademicYear, "id")
                ),
              });

              const changeAcademicYearHandler = () => {
                changeAcademicYear({ selectedYear: year });
              };

              return (
                <div
                  className={academicYearClass}
                  onClick={changeAcademicYearHandler}
                  data-test-id={`${componentTestId}-academicYear-${id}`}
                >
                  {label}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {_.size(userCurriculumPrograms) > 1 && (
        <div className={classes.curriculumContainer}>
          <div className={classes.curriculumHeader}>
            {t("common:select_with_label", {
              label: t("common:curriculum"),
            })}
          </div>
          <div className={classes.individualCurriculumContainer}>
            {_.map(userCurriculumPrograms, ({ id, type, acronym }) => {
              const individualCurriculumClass = classNames({
                [classes.individualCurriculum]: true,
                [classes.selectedCurriculum]: id == currentCurriculumProgramId,
              });

              const curriculumButtonClickHandler = () => {
                goToPlatformHomeRoute({
                  curriculumProgramId: id,
                });
              };

              return (
                <div
                  className={individualCurriculumClass}
                  onClick={curriculumButtonClickHandler}
                  data-test-id={`${componentTestId}-curriculum-${id}`}
                >
                  <CurriculumProgramLogo
                    type={type}
                    acronym={acronym}
                    height="32px"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {sessionVerificationDetails.isVisible && (
        <VerifyOtpModal
          onModalClose={onVerifyOtpModalClose}
          onInputChange={updateSessionVerificationDetails}
          inputDetails={sessionVerificationDetails}
          onSubmit={handleVerifySessionWithServer}
          generateEmailOTPForSessionVerification={
            generateEmailOTPForSessionVerification
          }
        />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const { id: userId, userEntityType } = getUserInfo();
  const orgWorkSpaceDetails = getOrgWorkSpaceDetails();

  let organizationId = "";
  if (orgWorkSpaceDetails) {
    organizationId = orgWorkSpaceDetails.organizationId;
  }

  const sessionVerificationDetails = _.get(
    state,
    "login.sessionVerificationDetails"
  );

  const selectedChild = _.get(state, "courses.selectedChild", {});

  return {
    userId,
    userEntityType,
    organizationId,
    sessionVerificationDetails,
    selectedChild,
  };
};

const mapActionCreators = {
  goToPlatformHomeRoute,
  verifySessionWithServer,
  updateSessionVerificationDetails,
  generateEmailOTPForSessionVerification,
  updateSelectedChild,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({
    resource: ["common"],
  }),
  graphql(getPlatformUserSessionDetailsQuery, {
    name: "getPlatformUserSessionDetails",
    options: ({ userId, userEntityType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
      },
    }),
    props({ getPlatformUserSessionDetails }) {
      const variables = _.get(getPlatformUserSessionDetails, "variables", {});
      const data = getPlatformUserSessionDetailsFromCache(variables);
      const isSessionVerified = _.get(data, "isSessionVerified", true);
      return {
        isSessionVerified,
      };
    },
  })
)(ChildSelector);
